import {
  Tooltip as V2Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@foresightdata/web-common-components';
import { Tooltip as MuiTooltip, TooltipProps as MuiTooltipProps } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CSSProperties, ReactElement } from 'react';
import { MaggieFeatureFlags } from '../../util/feature-flags';

/**
 * The app's Tooltip (FD-6107). Takes MUI's props and renders MUI's Tooltip, or, when the
 * `useV2Components` flag is on, web-common's Tooltip (Base UI) under the maggie-og theme, which
 * draws the app's white-card tooltip. Call sites switch to this wrapper unchanged.
 *
 * Deliberately the props the app uses: `title`, `placement`, the delays, controlled `open`, and
 * `maxWidth` in place of `componentsProps.tooltip.sx.maxWidth`. As in MUI, an empty title renders
 * the child alone, and the child must take a ref and the trigger's event props (any element,
 * MUI icon, or one of the app's wrappers).
 */
export interface TooltipProps extends Pick<
  MuiTooltipProps,
  'title' | 'placement' | 'enterDelay' | 'leaveDelay' | 'open' | 'children'
> {
  /** Without MUI's event: the web-common tooltip has none to give, and no call site reads it. */
  onOpen?: () => void;
  onClose?: () => void;
  /** The tooltip's width cap (MUI's is 300px); `'none'` lets wide content through. */
  maxWidth?: CSSProperties['maxWidth'];
}

type Side = 'top' | 'bottom' | 'left' | 'right';
type Align = 'start' | 'center' | 'end';

// MUI's placement ("top-start") is Base UI's side plus align.
function toSideAndAlign(placement: NonNullable<TooltipProps['placement']>): { side: Side; align: Align } {
  const [side, align] = placement.split('-') as [Side, 'start' | 'end' | undefined];
  return { side, align: align ?? 'center' };
}

// MUI's defaults: placement bottom, 100ms to open, no delay to close.
export function Tooltip({
  title,
  placement = 'bottom',
  enterDelay = 100,
  leaveDelay = 0,
  open,
  onOpen,
  onClose,
  maxWidth,
  children,
}: TooltipProps) {
  const { useV2Components } = useFlags<MaggieFeatureFlags>();

  if (!useV2Components) {
    return (
      <MuiTooltip
        title={title}
        placement={placement}
        enterDelay={enterDelay}
        leaveDelay={leaveDelay}
        open={open}
        onOpen={onOpen}
        onClose={onClose}
        componentsProps={maxWidth !== undefined ? { tooltip: { sx: { maxWidth } } } : undefined}
      >
        {children}
      </MuiTooltip>
    );
  }

  // As MUI: no title, no tooltip.
  if (title === '' || title == null || title === false) {
    return children;
  }

  const { side, align } = toSideAndAlign(placement);
  // Base UI keeps the delays on the provider; one per tooltip gives MUI's per-tooltip delays.
  return (
    <TooltipProvider delay={enterDelay} closeDelay={leaveDelay}>
      <V2Tooltip
        open={open}
        onOpenChange={(nextOpen) => {
          if (nextOpen) {
            onOpen?.();
          } else {
            onClose?.();
          }
        }}
      >
        <TooltipTrigger render={children as ReactElement} />
        <TooltipContent
          side={side}
          align={align}
          showArrow={false}
          style={maxWidth !== undefined ? { maxWidth } : undefined}
        >
          {title}
        </TooltipContent>
      </V2Tooltip>
    </TooltipProvider>
  );
}
